import { STREAMING_EVENT_TYPES } from '../constants';

/**
 * How long one DANI answer took, measured in the browser from the moment the
 * question is sent. Three points: the first event of any kind (DANI started),
 * the first answer content (the user sees something), and the end. Kept pure
 * so the timings can be tested without a stream; `useChatStreaming` feeds it.
 */

/** What kind of send this was, so slow answers can be grouped by cause. */
export type AnswerKind = 'chat' | 'planner' | 'experimental' | 'silent';

export type AnswerOutcome = 'done' | 'error' | 'no_answer' | 'stopped';

export interface AnswerTiming {
    kind: AnswerKind;
    outcome: AnswerOutcome;
    isNewConversation: boolean;
    /** Milliseconds from send to the end, the metric's value. */
    totalMs: number;
    /** Milliseconds from send to the first event of any kind; null if none came. */
    firstEventMs: number | null;
    /** Milliseconds from send to the first answer content; null if none came. */
    firstContentMs: number | null;
    /** How many events arrived (status lines, text pieces, charts, ...). */
    events: number;
}

/** Events that put part of the answer itself on screen. */
const CONTENT_EVENTS: ReadonlySet<string> = new Set([
    STREAMING_EVENT_TYPES.TEXT,
    STREAMING_EVENT_TYPES.DELTA_CONTENT,
    STREAMING_EVENT_TYPES.CONTENT_ARRAY,
    STREAMING_EVENT_TYPES.CHART,
    STREAMING_EVENT_TYPES.TABLE,
    STREAMING_EVENT_TYPES.AGENT_OPTIONS,
    STREAMING_EVENT_TYPES.PLAN_SUMMARY,
]);

export function answerKind(request: {
    isPlanner: boolean;
    isExperimental: boolean;
    silent?: boolean;
}): AnswerKind {
    if (request.silent) return 'silent';
    if (request.isPlanner) return 'planner';
    if (request.isExperimental) return 'experimental';
    return 'chat';
}

export interface AnswerTimer {
    /** Note an event from the stream. */
    event: (type: string) => void;
    /** The answer ended. Returns its timing the first time only, then null. */
    finish: (outcome: AnswerOutcome) => AnswerTiming | null;
}

export function startAnswerTimer(
    kind: AnswerKind,
    isNewConversation: boolean,
    now: () => number,
): AnswerTimer {
    const sentAt = now();
    let firstEventMs: number | null = null;
    let firstContentMs: number | null = null;
    let events = 0;
    let finished = false;

    return {
        event(type) {
            if (finished) return;
            const at = Math.round(now() - sentAt);
            events += 1;
            if (firstEventMs === null) firstEventMs = at;
            if (firstContentMs === null && CONTENT_EVENTS.has(type)) firstContentMs = at;
        },
        finish(outcome) {
            if (finished) return null;
            finished = true;
            return {
                kind,
                outcome,
                isNewConversation,
                totalMs: Math.round(now() - sentAt),
                firstEventMs,
                firstContentMs,
                events,
            };
        },
    };
}

/** Elapsed time as m:ss, e.g. 0:07 or 12:30. */
export function formatElapsed(ms: number): string {
    const seconds = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
